// src/pages/BlogList.js

import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Calendar, Clock, ArrowRight, 
  Sparkles, Phone 
} from 'lucide-react';
import { BLOG_POSTS } from '../data/blogData';
import '../styles/Blog.scss';

export default function BlogList() {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'School Guide', 'Admissions & Advice', 'Early Education', 'Academic Results', 'Campus & Facilities', 'Technology & Labs', 'Safety & Care'];

  const filteredPosts = selectedCategory === 'All' 
    ? BLOG_POSTS 
    : BLOG_POSTS.filter(post => post.category.toLowerCase().includes(selectedCategory.toLowerCase()));

  const featuredPost = BLOG_POSTS[0];
  const regularPosts = filteredPosts.filter(post => post.id !== featuredPost.id || selectedCategory !== 'All');

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Prabhat Tara Vidya Niketan Educational Blog & School Guides",
    "description": "Comprehensive school guides, parenting tips, board exam preparation strategies, and admission insights from the best private school in Goraul, Vaishali.",
    "url": "https://prabhattaravidyaniketan.in/blogs",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Prabhat Tara Vidya Niketan",
      "url": "https://prabhattaravidyaniketan.in"
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": BLOG_POSTS.map((post, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://prabhattaravidyaniketan.in/blog/${post.slug}`,
        "name": post.title
      }))
    }
  };

  return (
    <div className="blog-list-page">
      <Helmet>
        <title>Educational Blogs & School Guides | No. 1 Private School in Goraul Vaishali</title>
        <meta 
          name="description" 
          content="Explore expert articles on choosing the no. 1 school in Goraul, fee comparisons, nursery admissions, and board exam success from the no. 1 private school in Goraul, Vaishali." 
        />
        <meta 
          name="keywords" 
          content="no. 1 private school in goraul, no. 1 school in goraul, no. 1 school near goraul, no. 1 private school in goraul vaishali, no. 1 school in goraul vaishali, no. 1 school near goraul vaishali, best private school in goraul, best school in goraul, best school near goraul, best private school in goraul vaishali, best school in goraul vaishali, best school near goraul vaishali, school admission goraul, education blogs vaishali" 
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/blogs" />
        <meta property="og:title" content="Educational Blogs & School Guides | No. 1 School in Goraul Vaishali" />
        <meta property="og:description" content="Discover why Prabhat Tara Vidya Niketan is the No. 1 private school in Goraul. Read our parent guides, admission tips, and academic articles." />
        <meta property="og:url" content="https://prabhattaravidyaniketan.in/blogs" />
        <meta property="og:image" content="https://prabhattaravidyaniketan.in/descriptive-image/school-building-front-view.jpg" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Banner */}
      <section className="blog-hero">
        <div className="hero-container">
          <div className="hero-pill">
            <Sparkles size={14} />
            <span>Official Educational Knowledge Hub</span>
          </div>
          <h1>Insights, Advice & School Guides for Parents in Goraul & Vaishali</h1>
          <p>
            Your comprehensive resource for choosing the best private school in Goraul, understanding modern 
            teaching methodologies, board exam preparation, and child development.
          </p>
          <div className="keyword-badge-row">
            <span>#No1SchoolInGoraul</span>
            <span>#No1PrivateSchoolInGoraul</span>
            <span>#No1SchoolNearGoraul</span>
            <span>#BestSchoolInGoraulVaishali</span>
            <span>#PTVNGoraul</span>
          </div>
        </div>
      </section>

      {/* Main Container */}
      <div className="blog-container">
        
        {/* Category Controls */}
        <div className="blog-controls">
          <div className="categories-wrap">
            {categories.map(cat => (
              <button 
                key={cat} 
                className={selectedCategory === cat ? 'active' : ''}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="count-indicator">
            Showing {filteredPosts.length} Educational Guides
          </div>
        </div>

        {/* Featured Blog (shown when 'All' selected) */}
        {selectedCategory === 'All' && featuredPost && (
          <article className="featured-blog-card">
            <div className="featured-media">
              <img src={featuredPost.coverImage} alt={featuredPost.title} loading="lazy" />
              <span className="badge-featured">Featured Guide</span>
            </div>
            <div className="featured-body">
              <div className="meta-row">
                <span><Calendar size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> {featuredPost.publishDate}</span>
                <span>•</span>
                <span><Clock size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> {featuredPost.readTime}</span>
                <span>•</span>
                <span style={{ color: '#0c278c', fontWeight: 600 }}>{featuredPost.category}</span>
              </div>
              <h2>
                <Link to={`/blog/${featuredPost.slug}`}>
                  {featuredPost.title}
                </Link>
              </h2>
              <p>{featuredPost.excerpt}</p>
              <Link to={`/blog/${featuredPost.slug}`} className="read-cta">
                <span>Read Full Article</span>
                <ArrowRight size={15} />
              </Link>
            </div>
          </article>
        )}

        {/* Regular Posts Grid */}
        <div className="blog-grid">
          {(selectedCategory === 'All' ? regularPosts : filteredPosts).map(post => (
            <article key={post.id} className="blog-card">
              <div className="card-img-wrap">
                <img src={post.coverImage} alt={post.title} loading="lazy" />
                <span className="category-tag">{post.category}</span>
              </div>
              <div className="card-body">
                <div className="card-meta">
                  <span><Calendar size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} /> {post.publishDate}</span>
                  <span>•</span>
                  <span><Clock size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} /> {post.readTime}</span>
                </div>
                <h3>
                  <Link to={`/blog/${post.slug}`}>
                    {post.title}
                  </Link>
                </h3>
                <p>{post.excerpt}</p>
                <div className="card-footer">
                  <span className="primary-kw">{post.primaryKeyword}</span>
                  <Link to={`/blog/${post.slug}`} className="read-link">
                    <span>Read</span>
                    <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Bottom Call to Action */}
        <section className="blog-listing-cta">
          <h2>Looking for the Best Private School in Goraul for Your Child?</h2>
          <p>
            Experience 30+ years of educational excellence, dedicated faculty, modern science & computer labs, 
            and 100% board examination success at Prabhat Tara Vidya Niketan (Rajkhand, Goraul).
          </p>
          <div className="cta-button-group">
            <Link to="/admission-enquiry" className="btn-primary">
              Apply for Admission (2026-27)
            </Link>
            <a href="tel:7654164726" className="btn-secondary">
              <Phone size={15} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />
              Call Admissions: +91 76541 64726
            </a>
          </div>
        </section>

      </div>
    </div>
  );
}
